import React from 'react'
import { Box, Card, Chip, Button } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts'
import FlagIcon from '@mui/icons-material/Flag'
import TuneIcon from '@mui/icons-material/Tune'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

/**
 * PlanObjectifPatrimoine (LAP-CP07)
 * ---------------------------------
 * Plan d'atterrissage vers un OBJECTIF DE PATRIMOINE (montant + horizon) défini
 * par l'utilisateur dans « Mes Objectifs ». Affiche :
 *   - le verdict honnête (atteignable / ambitieux / hors de portée),
 *   - l'épargne mensuelle requise vs l'épargne actuelle,
 *   - le graphique tendance actuelle vs trajectoire requise + ligne objectif,
 *   - quand l'objectif n'est pas atteignable : le MAXIMUM FAISABLE et les
 *     leviers concrets pour le rendre réaliste,
 *   - un CTA pour créer le projet d'épargne correspondant.
 *
 * Toutes les valeurs viennent du moteur (buildPatrimoinePlan) ; le composant ne
 * fait que de l'affichage. La devise est formatée via formatMoney (profil pays).
 */
function PlanObjectifPatrimoine({ plan, formatMoney, onCreateProject, onAdaptObjective }) {
  const { t } = useTranslation('layout')
  if (!plan) return null
  const money = formatMoney || fallbackMoney

  const verdictMap = {
    reachable: { color: '#4ade80', bg: 'rgba(34,197,94,0.18)', icon: CheckCircleIcon },
    tight: { color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', icon: WarningAmberIcon },
    hard: { color: '#fb923c', bg: 'rgba(251,146,60,0.18)', icon: WarningAmberIcon },
    unreachable: { color: '#f87171', bg: 'rgba(248,113,113,0.18)', icon: ErrorOutlineIcon },
  }
  const vd = verdictMap[plan.verdict] || verdictMap.tight
  const VerdictIcon = vd.icon
  const hasGap = plan.gap > 0

  const riskLabel = t(`conseillerPatrimonial.planObjectif.risk.${plan.riskKey}`)

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      {/* En-tête */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <WTypography
            variant="overline"
            sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}
          >
            {t('conseillerPatrimonial.planObjectif.overline')}
          </WTypography>
          <WTypography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerPatrimonial.planObjectif.title', {
              amount: money(plan.targetAmount),
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 0.5 }}>
            {t('conseillerPatrimonial.planObjectif.subtitle', {
              years: plan.horizonYears,
              date: plan.targetDateLabel || '—',
            })}
            {plan.objectiveName ? ` · ${plan.objectiveName}` : ''}
          </WTypography>
        </Box>

        <Chip
          icon={<VerdictIcon sx={{ color: 'inherit !important' }} />}
          label={t(`conseillerPatrimonial.planObjectif.verdict.${plan.verdict}`)}
          sx={{
            bgcolor: vd.bg,
            color: vd.color,
            fontWeight: 700,
            fontSize: 13,
            height: 32,
            px: 1,
          }}
        />
      </Box>

      <WTypography variant="caption" sx={{ color: '#8da3c9 !important' }}>
        {t('conseillerPatrimonial.planObjectif.assumption', {
          rate: plan.expectedReturnPct,
          profile: riskLabel,
        })}
      </WTypography>

      {/* KPIs */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }}
        gap={2}
        mt={2}
      >
        <KPI
          label={t('conseillerPatrimonial.planObjectif.kpiCurrentNetWorth')}
          value={money(plan.currentNetWorth)}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectif.kpiCurrentMonthly')}
          value={money(plan.currentMonthlySavings)}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectif.kpiRequiredMonthly')}
          value={money(plan.requiredMonthlySavings)}
          color={hasGap ? '#fbbf24' : '#4ade80'}
          highlight
        />
        <KPI
          label={
            hasGap
              ? t('conseillerPatrimonial.planObjectif.kpiGap')
              : t('conseillerPatrimonial.planObjectif.kpiSurplus')
          }
          value={money(Math.abs(plan.gap))}
          color={hasGap ? '#f87171' : '#4ade80'}
          highlight
        />
      </Box>

      {/* Graphique : tendance actuelle vs trajectoire requise + ligne objectif */}
      <Box sx={{ height: 300, mt: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={plan.chart} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="year" stroke="#a0b4d6" tick={{ fontSize: 11 }} />
            <YAxis
              stroke="#a0b4d6"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => money(v)}
              width={70}
            />
            <Tooltip
              contentStyle={{
                background: '#0a1429',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                color: '#dbe5f7',
              }}
              formatter={(v) => money(v)}
            />
            <Legend wrapperStyle={{ color: '#dbe5f7', fontSize: 12 }} iconType="circle" />
            <ReferenceLine
              y={plan.targetAmount}
              stroke="#60a5fa"
              strokeDasharray="6 4"
              label={{
                value: t('conseillerPatrimonial.planObjectif.chartTarget'),
                fill: '#93c5fd',
                fontSize: 11,
                position: 'insideTopLeft',
              }}
            />
            <Line
              type="monotone"
              dataKey="tendance"
              name={t('conseillerPatrimonial.planObjectif.chartCurrent')}
              stroke="#f59e0b"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5 }}
            />
            {hasGap && (
              <Line
                type="monotone"
                dataKey="requis"
                name={t('conseillerPatrimonial.planObjectif.chartRequired')}
                stroke="#4ade80"
                strokeWidth={3}
                strokeDasharray="5 4"
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Bloc verdict détaillé */}
      {hasGap ? (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            bgcolor: 'rgba(96,165,250,0.06)',
            border: '1px solid rgba(96,165,250,0.2)',
          }}
        >
          <WTypography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('conseillerPatrimonial.planObjectif.maxFeasibleTitle')}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mb: 2 }}>
            {t('conseillerPatrimonial.planObjectif.maxFeasibleBody', {
              monthly: money(plan.currentMonthlySavings),
              amount: money(plan.projectedWithCurrent),
              years: plan.horizonYears,
            })}
          </WTypography>

          <WTypography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('conseillerPatrimonial.planObjectif.toMakeRealisticTitle')}
          </WTypography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#c1d0eb' }}>
            {plan.extraMonthlyNeeded > 0 && (
              <Lever
                text={t('conseillerPatrimonial.planObjectif.leverSave', {
                  amount: money(plan.extraMonthlyNeeded),
                  total: money(plan.requiredMonthlySavings),
                })}
              />
            )}
            {plan.realisticHorizonYears &&
              plan.realisticHorizonYears > plan.horizonYears && (
                <Lever
                  text={t('conseillerPatrimonial.planObjectif.leverHorizon', {
                    years: plan.realisticHorizonYears,
                  })}
                />
              )}
            <Lever
              text={t('conseillerPatrimonial.planObjectif.leverTarget', {
                amount: money(plan.realisticTarget),
                years: plan.horizonYears,
              })}
            />
            {plan.requiredReturnPct != null &&
              plan.requiredReturnPct > plan.expectedReturnPct && (
                <Lever
                  text={t('conseillerPatrimonial.planObjectif.leverReturn', {
                    rate: plan.requiredReturnPct,
                  })}
                />
              )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            bgcolor: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.2)',
          }}
        >
          <WTypography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#4ade80 !important' }}>
            {t('conseillerPatrimonial.planObjectif.onTrackTitle')}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
            {t('conseillerPatrimonial.planObjectif.onTrackBody', {
              monthly: money(plan.requiredMonthlySavings),
              amount: money(plan.targetAmount),
              years: plan.horizonYears,
            })}
          </WTypography>
        </Box>
      )}

      {/* CTA : créer un projet financier (+ adapter l'objectif) */}
      {onCreateProject && (
        <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
          {onAdaptObjective && (
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={onAdaptObjective}
              sx={{
                color: '#dbe5f7',
                borderColor: 'rgba(255,255,255,0.2)',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { borderColor: '#dbe5f7', bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              {t('conseillerPatrimonial.planObjectif.ctaAdaptObjective')}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<FlagIcon />}
            onClick={() => onCreateProject(plan)}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' },
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            {t('conseillerPatrimonial.planObjectif.ctaCreateProject')}
          </Button>
        </Box>
      )}
    </Card>
  )
}

function Lever({ text }) {
  return (
    <li style={{ marginBottom: 6 }}>
      <WTypography variant="body2" component="span" sx={{ color: '#c1d0eb !important' }}>
        {text}
      </WTypography>
    </li>
  )
}

function KPI({ label, value, color, highlight }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: highlight ? `${color}15` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${highlight ? color + '40' : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
        {label}
      </WTypography>
      <WTypography
        variant="h6"
        sx={{ fontWeight: 700, color: `${color} !important`, mt: 0.5 }}
      >
        {value}
      </WTypography>
    </Box>
  )
}

function fallbackMoney(n) {
  if (n === null || n === undefined) return '—'
  return `${n}`
}

export default PlanObjectifPatrimoine
